import { msg } from "@lingui/core/macro";
import { createRootRoute, Outlet, useLocation } from "@tanstack/solid-router";
import { createEffect, Suspense } from "solid-js";
import { installNewTabAccountHandoff } from "@/lib/new-tab-handoff";
import { Toaster } from "@/components/ui/toast";
import { InitialSyncOverlay } from "@/components/initial-sync-overlay";
import { AlertCircle, RefreshCw } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { BootManager } from "@/providers/boot-manager";
import { AppLockProvider, GlassSurface, PageTransition } from "@fleet/ui";
import { AppBackground } from "@/components/app-background";
import { AppErrorBoundary } from "@/components/app-error-boundary";

function RootErrorComponent(props: { error: unknown; reset: () => void }) {
  const { t } = useI18n();
  createEffect(() => {
    console.error("[RootErrorComponent] Caught route error:", props.error);
  });
  return (
    <div class="min-h-dvh flex items-center justify-center p-4 bg-background">
      <GlassSurface class="max-w-md w-full p-8 flex flex-col items-center text-center space-y-6">
        <div class="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertCircle class="h-8 w-8 text-destructive" />
        </div>
        <div class="space-y-2">
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Something went wrong`)}</h2>
          <p class="text-muted-foreground text-sm">
            {props.error instanceof Error
              ? props.error.message
              : t(msg`An unexpected error has occurred.`)}
          </p>
        </div>
        <div class="flex gap-3 w-full">
          <Button
            variant="outline"
            class="flex-1"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            {t(msg`Reload page`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

function RootComponent() {
  const location = useLocation();

  return (
    <div class="relative min-h-dvh w-full">
      <AppBackground class="flex flex-col min-h-dvh">
        <AppErrorBoundary label="root">
          <Suspense fallback={null}>
            <BootManager>
              <AppLockProvider>
                <InitialSyncOverlay>
                  <PageTransition
                    routeKey={(() => {
                      const path = location().pathname;
                      if (path.startsWith("/login") || path.startsWith("/register")) {
                        return "auth";
                      }
                      return "app";
                    })()}
                    class="flex flex-1 flex-col min-h-0 h-full"
                  >
                    <Outlet />
                  </PageTransition>
                </InitialSyncOverlay>
              </AppLockProvider>
            </BootManager>
          </Suspense>
        </AppErrorBoundary>
        <Toaster />
      </AppBackground>
    </div>
  );
}

export const rootRoute = createRootRoute({
  beforeLoad: () => {
    installNewTabAccountHandoff();
  },
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
